import React, { useRef, useEffect } from "react"
import { UseFormRegister, UseFormGetFieldState, Path, UseFormWatch, UseFormSetValue } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { PlaceOrderFormData, Ticket, TicketQuestion } from "types"
import { ReactComponent as Check } from "../../assets/check.svg"
import {
  sanitizeFieldName,
  shouldDisableContactField,
  getFieldMaxLength,
  getFieldMaxLengthMessageKey,
  getFieldMaxLengthDefaultMessage,
  shouldValidateEmail,
  EMAIL_VALIDATION_PATTERN,
} from "utils/form-submission/contact-field-utils"
import { detectUnifiedContactFields } from "utils/form-submission/contact-field-mapper"

interface BasicTicketCardProps {
  ticket: Ticket
  ticketIndex: number
  register: UseFormRegister<PlaceOrderFormData>
  getFieldState: UseFormGetFieldState<PlaceOrderFormData>
  isSameAsContact: boolean
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  isFirstTicketOfType: boolean
  watch?: UseFormWatch<PlaceOrderFormData>
  setValue?: UseFormSetValue<PlaceOrderFormData>
}

const BasicTicketCard: React.FC<BasicTicketCardProps> = ({
  ticket,
  ticketIndex,
  register,
  getFieldState,
  isSameAsContact,
  handleCheckboxChange,
  isFirstTicketOfType,
  watch,
  setValue,
}) => {
  const { t } = useTranslation()
  const checkboxRef = useRef<HTMLInputElement>(null)
  const isFirstTicket = isFirstTicketOfType

  // Watch contact information fields if watch function is provided
  const contactFirstName = watch?.("contactInfo.firstName")
  const contactLastName = watch?.("contactInfo.lastName")
  const contactEmail = watch?.("contactInfo.email")

  // Auto-populate first ticket fields when "same as contact" is checked
  useEffect(() => {
    if (!isSameAsContact || !isFirstTicket || !setValue) return

    // Use unified contact field detection for basic forms
    const mapping = detectUnifiedContactFields(
      ticket.questions,
      ticket.id,
      false // isAdvancedFormsEnabled = false for basic forms
    )

    if (!mapping) return

    // Auto-populate contact fields using the unified mapping
    if (mapping.firstName && contactFirstName?.trim()) {
      setValue(
        mapping.firstName.fieldName as Path<PlaceOrderFormData>,
        contactFirstName.trim(),
        { shouldValidate: true }
      )
    }

    if (mapping.lastName && contactLastName?.trim()) {
      setValue(
        mapping.lastName.fieldName as Path<PlaceOrderFormData>,
        contactLastName.trim(),
        { shouldValidate: true }
      )
    }

    if (mapping.email && contactEmail?.trim()) {
      setValue(
        mapping.email.fieldName as Path<PlaceOrderFormData>,
        contactEmail.trim(),
        { shouldValidate: true }
      )
    }
  }, [isSameAsContact, contactFirstName, contactLastName, contactEmail, isFirstTicket, ticket.questions, ticket.id, setValue])

  return (
    <div
      key={ticket.id}
      className="overflow-visible p-4 bg-white rounded-lg border shadow-sm border-progress_bar_background text-default_card_text"
      role="region"
      aria-label={`${t("general.ticket")} ${ticket.ticket_name}`}
    >
      <h4
        className="mb-4 text-lg font-semibold text-black"
        id={`ticket-heading-${ticket.id}`}
      >
        {ticket.ticket_name} #{ticketIndex + 1}
      </h4>
      {isFirstTicket && (
        <div className="mb-4">
          <label className="flex items-center text-sm cursor-pointer">
            <input
              ref={checkboxRef}
              type="checkbox"
              id="same-as-contact-checkbox"
              className="sr-only"
              onChange={handleCheckboxChange}
              checked={isSameAsContact}
              aria-labelledby="same-as-contact-label"
            />
            <div
              className={`relative w-6 h-6 ${
                isSameAsContact ? "bg-theme_accent" : "bg-gray-300"
              } rounded-md transition mr-2`}
              onClick={() => {
                // Ensure keyboard focus lands on the actual checkbox for accessibility
                checkboxRef.current?.focus()
              }}
              aria-hidden="true"
            >
              {isSameAsContact && (
                <Check
                  className="absolute top-1/2 left-1/2 w-4 h-4 transform -translate-x-1/2 -translate-y-1/2"
                  fill="var(--theme_accent_contrast)"
                />
              )}
            </div>
            <span id="same-as-contact-label">
              {t("details.contact_info_same")}
            </span>
          </label>
        </div>
      )}
      <div className="pb-4 space-y-4">
        {(ticket.questions || [])
          .sort((a: TicketQuestion, b: TicketQuestion) => a.rank - b.rank)
          .map((question: TicketQuestion) => (
            <div key={question.id}>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                {question.text}
                {question.required && (
                  <span className="ml-1 text-red-500">*</span>
                )}
              </label>
              <input
                type={question.type || "text"}
                disabled={shouldDisableContactField(
                  question.editable,
                  isSameAsContact,
                  isFirstTicket
                )}
                className={`w-full border border-input_border rounded-md p-2 ${
                  shouldDisableContactField(
                    question.editable,
                    isSameAsContact,
                    isFirstTicket
                  )
                    ? "bg-gray-100 cursor-not-allowed"
                    : ""
                }`}
                aria-label={`${question.required ? "Required" : "Optional"}: ${
                  question.text
                }`}
                {...register(
                  `tickets.${ticket.id}.${sanitizeFieldName(
                    question.text
                  )}` as Path<PlaceOrderFormData>,
                  {
                    required: question.required,
                    maxLength: {
                      value: getFieldMaxLength(question.text),
                      message: t(
                        getFieldMaxLengthMessageKey(question.text),
                        getFieldMaxLengthDefaultMessage(question.text)
                      ),
                    },
                    ...(shouldValidateEmail(question.text, question.type)
                      ? {
                          pattern: {
                            value: EMAIL_VALIDATION_PATTERN,
                            message: t(
                              "validation.email_invalid",
                              "Invalid email address"
                            ),
                          },
                        }
                      : {}),
                  }
                )}
              />
              {(() => {
                const fieldName = `tickets.${ticket.id}.${sanitizeFieldName(
                  question.text
                )}` as Path<PlaceOrderFormData>
                const fieldState = getFieldState(fieldName)
                return (
                  fieldState.error && (
                    <p className="mt-1 text-sm text-red-500">
                      {fieldState.error.message}
                    </p>
                  )
                )
              })()}
            </div>
          ))}
      </div>
    </div>
  )
}

export { BasicTicketCard }
